import styled from '@emotion/styled';
import { Model } from '@arra/core';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphTitle } from './ResultGraph';
import { getHumanizedValue } from './SeedsTooltipComponent';
import keyify from '../project/parameters/keyify';
import { co2Units, humanizeValue } from '../actions/page/ActionCard';

// duplicate of a similar function server side : both are to be updated !
export const parseValueAndUnit = (
  input: string | undefined,
): { value: number; unit: string } | null => {
  if (!input) {
    return null;
  }

  const match = input.match(/^([\d\.]+)\s*(\S+)$/);

  if (!match) {
    console.log(
      `Invalid input format. Expected format: '<number> <unit>'. Input is ${input}`,
    );
    return null;
  }

  const value = parseFloat(match[1]);
  const unit = match[2];

  return { value, unit };
};

const GlobalScoreContainer = styled.div`
  width: 100%;
  background-color: white;
  position: relative;
  margin: 16px 0;
  padding: 0 16px;
  > div:last-of-type {
    margin-top: 16px;
    width: 50%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    > p {
      flex: 1;
    }
    > p:first-of-type {
      text-align: center;
      background: var(--bg-score);
      padding: 6px 0;
      border-radius: 24px;
    }
  }
`;

interface GlobalScoreComponentProps {
  title?: string;
  subtitle?: string;
  score?: string;
  children: React.ReactNode;
  dashboard?: boolean;
  code?: string;
  model?: Model;
  uncertainty?: number;
  displayScore: boolean;
}

const GlobalScoreComponent = ({
  title,
  score,
  subtitle,
  uncertainty,
  children,
  dashboard,
  code,
  model,
  displayScore
}: GlobalScoreComponentProps) => {
  const { t, i18n } = useTranslation(['project', 'model']);
  const translationPrefix = `model:${keyify(model?.name).toLowerCase()}.result.${code?.toLowerCase()}`;

  const parsedScore = parseValueAndUnit(score);
  let displayedValue = '';
  let displayedUnit = '';

  if (parsedScore) {
    const impactScope = co2Units.has(parsedScore.unit) ? 'co2' : code || '';
    const humanizedScore = humanizeValue(
      parsedScore.unit,
      parsedScore.value,
      impactScope,
    );
    displayedValue = humanizedScore.value
    displayedUnit = humanizedScore.unit;
  }


  if (dashboard) {
    return (
      <div>
        <p className="h5r">{t(`${translationPrefix}.title`)}</p>
        <p className="h3b">{`${displayedValue} ${displayedUnit}`.trim()}</p>
      </div>
    );
  }

  return (
    <GlobalScoreContainer>
      {i18n.exists(`${translationPrefix}.title`) && (
        <GraphTitle
          text={t(`${translationPrefix}.title`)}
          size="full"
          color="default"
        />
      )}
      {i18n.exists(`${translationPrefix}.subtitle`) && (
        <p className="h6r">{t(`${translationPrefix}.subtitle`)}</p>
      )}
      <div>
        {displayScore && <p className="h3b">
          <b>{displayedValue}</b>
          {displayedUnit && <> {displayedUnit}</>}
          {uncertainty && <> ± {(uncertainty * 100).toFixed(0)}%</>}
        </p>}
        {children}
      </div>
    </GlobalScoreContainer>
  );
};


type ImpactCO2Props = {
  value: number;
};

export const ImpactCO2 = ({ value }: ImpactCO2Props) => {

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    // Clear previous script/widget on rerender
    ref.current.innerHTML = '';

    const s = document.createElement('script');
    s.setAttribute('name', 'impact-co2');
    s.src = 'https://impactco2.fr/iframe.js';
    s.setAttribute('data-type', 'comparateur/etiquette-animee');

    // Build data-search with your dynamic value
    s.setAttribute(
      'data-search',
      `?value=${value}&comparisons=avion-pny,francais,email,smartphone,voiturethermique&language=fr&theme=default`
    );

    ref.current.appendChild(s);
  }, [value]); // re-run when value changes

  return <div ref={ref} />;
}


export default GlobalScoreComponent;
